import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import StackCell from '../components/ui/StackCell.tsx';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';

const formatInvestmentCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'stocks':
      return 'Ações';
    case 'fixed_income':
      return 'Renda Fixa';
    case 'funds':
      return 'Fundos';
    case 'crypto':
      return 'Cripto';
    case 'offshore':
      return 'Exterior';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

const formatPhilanthropyCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'education':
      return 'Educação';
    case 'health':
      return 'Saúde';
    case 'poverty':
      return 'Combate à Pobreza';
    case 'culture':
      return 'Cultura';
    case 'environment':
      return 'Meio Ambiente';
    case 'religion':
      return 'Religião';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

const formatReceivableCategory = (category?: string): string => {
  switch (String(category || '').toLowerCase()) {
    case 'real_estate':
      return 'Imóvel';
    case 'vehicle':
      return 'Veículo';
    case 'mutuos':
      return 'Mútuos';
    case 'other':
      return 'Outros';
    default:
      return category || 'N/A';
  }
};

const REPORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'balance-sheet', label: 'Patrimônio consolidado' },
  { value: 'assets', label: 'Bens e ativos' },
  { value: 'vehicle-inventory', label: 'Estoque p/ venda' },
  { value: 'rentals', label: 'Aluguéis' },
  { value: 'vessels', label: 'Embarcações' },
  { value: 'aircrafts', label: 'Aeronaves' },
  { value: 'investments', label: 'Investimentos' },
  { value: 'philanthropies', label: 'Filantropia' },
  { value: 'companies', label: 'Empresas' },
  { value: 'debts', label: 'Dívidas' },
  { value: 'receivables', label: 'Contas a receber' },
];

const endpointFor = (reportType: string): string => {
  switch (reportType) {
    case 'balance-sheet':
      return '/reports/financial-summary';
    case 'vehicle-inventory':
      return '/reports/vehicles';
    case 'aircrafts':
      return '/reports/aircrafts';
    case 'vessels':
      return '/reports/vessels';
    case 'debts':
      return '/reports/debts';
    case 'receivables':
      return '/reports/receivables';
    case 'assets':
      return '/reports/assets';
    case 'companies':
      return '/reports/companies';
    case 'investments':
      return '/reports/investments';
    case 'philanthropies':
      return '/reports/philanthropies';
    case 'rentals':
      return '/reports/rentals';
    default:
      throw new Error('Tipo de relatório não suportado');
  }
};

const backendTypeFor = (reportType: string): string => {
  switch (reportType) {
    case 'balance-sheet':
      return 'financial-summary';
    case 'vehicle-inventory':
      return 'vehicles';
    default:
      return reportType;
  }
};

const getReportTitle = (reportType: string): string =>
  REPORT_OPTIONS.find(o => o.value === reportType)?.label || 'Relatório';

const StatusCell: React.FC<{ status?: string }> = ({ status }) => (
  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status || '')}`}>
    {formatStatus(status || '')}
  </span>
);

const NewReports: React.FC = () => {
  const [reportType, setReportType] = useState('balance-sheet');
  const [dateFrom, setDateFrom] = useState('2026-01-01');
  const [dateTo, setDateTo] = useState('2026-09-17');
  const [groupBy, setGroupBy] = useState('type');
  const [selectedReport, setSelectedReport] = useState<string>('');
  const [reportData, setReportData] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateReportMutation = useMutation(
    async ({ type }: { type: string }) => {
      const response = await api.get(endpointFor(type));
      return response.data;
    },
    {
      onSuccess: data => {
        setReportData(data);
        setIsGenerating(false);
      },
      onError: error => {
        console.error('Erro ao gerar relatório:', error);
        setIsGenerating(false);
      },
    }
  );

  const exportReportMutation = useMutation(
    async ({ type, format }: { type: string; format: 'excel' | 'pdf' }) => {
      const backendReportType = backendTypeFor(type);
      const response = await api.get(`/reports/export/${format}/${backendReportType}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type:
          format === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getReportTitle(type).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    {
      onError: error => {
        console.error('Erro ao exportar relatório:', error);
        alert('Erro ao exportar relatório. Tente novamente.');
      },
    }
  );

  const handleGenerateReport = () => {
    setSelectedReport(reportType);
    setIsGenerating(true);
    setReportData(null);
    generateReportMutation.mutate({ type: reportType });
  };

  const handleExportReport = (format: 'excel' | 'pdf') => {
    if (!selectedReport) return;
    exportReportMutation.mutate({ type: selectedReport, format });
  };

  const periodHint = useMemo(() => {
    try {
      const a = new Date(dateFrom + 'T00:00:00').toLocaleDateString('pt-BR');
      const b = new Date(dateTo + 'T00:00:00').toLocaleDateString('pt-BR');
      return `${a} – ${b}`;
    } catch {
      return '';
    }
  }, [dateFrom, dateTo]);

  const renderKpis = () => {
    if (!reportData || !selectedReport) return null;
    const s = reportData.summary || {};

    if (selectedReport === 'balance-sheet') {
      return (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Ativos totais</div>
            <div className="kpi-value">{formatCurrency(s.totalAssets || 0)}</div>
            <div className="kpi-hint">patrimônio bruto</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Passivos</div>
            <div className="kpi-value negative">{formatCurrency(s.totalLiabilities || 0)}</div>
            <div className="kpi-hint">dívidas + financiamentos</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Patrimônio líquido</div>
            <div className="kpi-value positive">{formatCurrency(s.netWorth || 0)}</div>
            <div className="kpi-hint">ativos − passivos</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Índice dívida/ativo</div>
            <div className="kpi-value">{(s.debtToAssetRatio || 0).toFixed(1)}%</div>
            <div className="kpi-hint">{periodHint}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'vehicle-inventory') {
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Total de veículos</div>
            <div className="kpi-value">{s.totalVehicles || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de venda</div>
            <div className="kpi-value">{formatCurrency(s.totalSellingValue || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de compra</div>
            <div className="kpi-value">{formatCurrency(s.totalPurchaseValue || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'aircrafts') {
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Total de aeronaves</div>
            <div className="kpi-value">{s.totalAircrafts || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de venda</div>
            <div className="kpi-value">{formatCurrency(s.totalSellingValue || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de compra</div>
            <div className="kpi-value">{formatCurrency(s.totalPurchaseValue || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'vessels') {
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Total de embarcações</div>
            <div className="kpi-value">{s.totalVessels || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de venda</div>
            <div className="kpi-value">{formatCurrency(s.totalSellingValue || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de compra</div>
            <div className="kpi-value">{formatCurrency(s.totalPurchaseValue || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'debts') {
      return (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Total de dívidas</div>
            <div className="kpi-value">{s.totalDebts || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor total</div>
            <div className="kpi-value negative">{formatCurrency(s.totalAmount || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor restante</div>
            <div className="kpi-value negative">{formatCurrency(s.totalRemaining || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor pago</div>
            <div className="kpi-value positive">{formatCurrency(s.totalPaid || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'receivables') {
      return (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Total de recebíveis</div>
            <div className="kpi-value">{s.totalReceivables || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor original</div>
            <div className="kpi-value">{formatCurrency(s.totalAmount || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Saldo a receber</div>
            <div className="kpi-value positive">{formatCurrency(s.totalRemaining || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Em atraso</div>
            <div className="kpi-value negative">{formatCurrency(s.overdueAmount || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'assets') {
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Total de ativos</div>
            <div className="kpi-value">{s.totalAssets || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor total</div>
            <div className="kpi-value">{formatCurrency(s.totalValue || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor de compra</div>
            <div className="kpi-value">{formatCurrency(s.totalPurchaseValue || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'companies') {
      return (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Empresas</div>
            <div className="kpi-value">{s.totalCompanies || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Receita anual</div>
            <div className="kpi-value">{formatCurrency(s.totalAnnualRevenue || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Lucro anual</div>
            <div className="kpi-value positive">{formatCurrency(s.totalAnnualProfit || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valuation</div>
            <div className="kpi-value">{formatCurrency(s.totalValuation || 0)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'investments') {
      return (
        <div className="kpi-row">
          <div className="kpi">
            <div className="kpi-label">Investimentos</div>
            <div className="kpi-value">{s.totalInvestments || 0}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Investido</div>
            <div className="kpi-value">{formatCurrency(s.totalInvested || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor atual</div>
            <div className="kpi-value">{formatCurrency(s.totalCurrent || 0)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Rendimento</div>
            <div className="kpi-value positive">{(Number(s.yieldPercent || 0)).toFixed(2)}%</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'philanthropies') {
      const donations = (reportData.philanthropies || reportData.items || []) as any[];
      const totalDonations = s.totalPhilanthropies ?? donations.length;
      const totalDonated =
        s.totalDonatedAmount ??
        donations.reduce((sum, d) => sum + Number(d.donatedAmount || 0), 0);
      const totalAvailable =
        s.totalAvailableAmount ??
        donations.reduce(
          (sum, d) =>
            sum + Number(d.availableAmount ?? Number(d.amount || 0) - Number(d.donatedAmount || 0)),
          0
        );
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Doações</div>
            <div className="kpi-value">{totalDonations}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Total doado</div>
            <div className="kpi-value">{formatCurrency(totalDonated)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Disponível</div>
            <div className="kpi-value positive">{formatCurrency(totalAvailable)}</div>
          </div>
        </div>
      );
    }

    if (selectedReport === 'rentals') {
      const rentals = (reportData.rentals || reportData.items || []) as any[];
      const totalContracts = s.totalRentals ?? rentals.length;
      const totalMonthly =
        s.totalMonthlyRent ?? rentals.reduce((sum, r) => sum + (Number(r.monthlyRent) || 0), 0);
      return (
        <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="kpi">
            <div className="kpi-label">Contratos</div>
            <div className="kpi-value">{totalContracts}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor mensal</div>
            <div className="kpi-value positive">{formatCurrency(totalMonthly || 0)}</div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderTable = () => {
    if (!reportData || !selectedReport) return null;

    if (selectedReport === 'balance-sheet') {
      const assets = reportData.assets || {};
      const rows = [
        { cat: 'Imóveis / bens', items: '—', value: assets.fixedAssets || 0 },
        { cat: 'Frota + estoque', items: '—', value: assets.vehicles || 0 },
        { cat: 'Embarcações', items: '—', value: assets.vessels || 0 },
        { cat: 'Aeronaves', items: '—', value: assets.aircrafts || 0 },
        { cat: 'Investimentos', items: '—', value: assets.investments || 0 },
        { cat: 'Recebíveis', items: '—', value: assets.receivables || 0 },
      ];
      const total = Number(assets.total || rows.reduce((s, r) => s + Number(r.value || 0), 0)) || 1;
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Categoria</th>
                <th className="hide-m">Itens</th>
                <th style={{ textAlign: 'right' }} className="hide-m">
                  % do total
                </th>
                <th style={{ textAlign: 'right' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.cat}>
                  <td>
                    <strong>{r.cat}</strong>
                  </td>
                  <td className="mono hide-m">{r.items}</td>
                  <td style={{ textAlign: 'right' }} className="mono hide-m">
                    {((Number(r.value) / total) * 100).toFixed(1)}%
                  </td>
                  <td className="amount" style={{ textAlign: 'right' }}>
                    {formatCurrency(r.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'vehicle-inventory') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Veículo</th>
                <th className="hide-m">Ano</th>
                <th className="hide-m">Placa</th>
                <th style={{ textAlign: 'right' }} className="hide-m">
                  Compra
                </th>
                <th className="hide-m">Status</th>
                <th style={{ textAlign: 'right' }}>Venda</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.vehicles || []).map((vehicle: any) => (
                <tr key={vehicle.id}>
                  <td>
                    <StackCell
                      title={`${vehicle.make || vehicle.brand} ${vehicle.model}`}
                      meta={vehicle.year ? String(vehicle.year) : undefined}
                      stackExtra={
                        <>
                          {vehicle.licensePlate}
                          {' · '}
                          Compra {formatCurrency(vehicle.purchasePrice || 0)}
                        </>
                      }
                    />
                  </td>
                  <td className="hide-m">{vehicle.year}</td>
                  <td className="mono hide-m">{vehicle.licensePlate}</td>
                  <td style={{ textAlign: 'right' }} className="hide-m">
                    {formatCurrency(vehicle.purchasePrice || 0)}
                  </td>
                  <td className="hide-m">
                    <StatusCell status={vehicle.status} />
                  </td>
                  <td style={{ textAlign: 'right' }} className="amount">{formatCurrency(vehicle.sellingPrice || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'aircrafts') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Aeronave</th>
                <th className="hide-m">Ano</th>
                <th className="hide-m">Matrícula</th>
                <th className="hide-m">Status</th>
                <th style={{ textAlign: 'right' }}>Venda</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.aircrafts || []).map((aircraft: any) => (
                <tr key={aircraft.id}>
                  <td>
                    <strong>
                      {(aircraft.make || aircraft.brand)
                        ? `${aircraft.make || aircraft.brand} ${aircraft.model || ''}`.trim()
                        : aircraft.model || 'N/A'}
                    </strong>
                  </td>
                  <td className="hide-m">{aircraft.year}</td>
                  <td className="mono">{aircraft.tailNumber}</td>
                  <td className="hide-m">
                    <StatusCell status={aircraft.status} />
                  </td>
                  <td style={{ textAlign: 'right' }} className="amount">{formatCurrency(aircraft.sellingPrice || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'vessels') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Embarcação</th>
                <th className="hide-m">Ano</th>
                <th className="hide-m">Matrícula</th>
                <th className="hide-m">Status</th>
                <th style={{ textAlign: 'right' }}>Venda</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.vessels || []).map((vessel: any) => (
                <tr key={vessel.id}>
                  <td>
                    <strong>
                      {(vessel.make || vessel.brand)
                        ? `${vessel.make || vessel.brand} ${vessel.model || ''}`.trim()
                        : vessel.model || 'N/A'}
                    </strong>
                  </td>
                  <td className="hide-m">{vessel.year}</td>
                  <td className="mono">{vessel.registrationNumber}</td>
                  <td className="hide-m">
                    <StatusCell status={vessel.status} />
                  </td>
                  <td style={{ textAlign: 'right' }} className="amount">{formatCurrency(vessel.sellingPrice || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'debts') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="hide-m">Credor</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Restante</th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.debts || []).map((debt: any) => (
                <tr key={debt.id}>
                  <td>
                    <strong>{debt.description}</strong>
                  </td>
                  <td className="hide-m">{debt.creditor}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(debt.amount || 0)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(debt.remainingAmount || 0)}</td>
                  <td className="hide-m">
                    <StatusCell status={debt.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'receivables') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="hide-m">Cliente</th>
                <th className="hide-m">Categoria</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.receivables || []).map((r: any) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.description}</strong>
                  </td>
                  <td className="hide-m">{r.customer || r.customerName}</td>
                  <td className="hide-m">{formatReceivableCategory(r.category || r.type)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(r.currentAmount ?? r.remainingAmount ?? 0)}
                  </td>
                  <td className="hide-m">
                    <StatusCell status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'assets') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Nome</th>
                <th className="hide-m">Categoria</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.assets || []).map((asset: any) => (
                <tr key={asset.id}>
                  <td>
                    <strong>{asset.name || asset.description}</strong>
                  </td>
                  <td className="hide-m">{asset.category || asset.type}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(asset.currentValue || 0)}</td>
                  <td className="hide-m">
                    <StatusCell status={asset.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'companies') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Empresa</th>
                <th className="hide-m">CNPJ</th>
                <th style={{ textAlign: 'right' }}>Valuation</th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.companies || []).map((company: any) => (
                <tr key={company.id}>
                  <td>
                    <strong>{company.name}</strong>
                  </td>
                  <td className="mono hide-m">{company.cnpj}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(company.valuation || 0)}</td>
                  <td className="hide-m">
                    <StatusChip
                      label={company.status === 'active' ? 'Ativa' : 'Inativa'}
                      tone={company.status === 'active' ? 'green' : 'slate'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'investments') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="hide-m">Categoria</th>
                <th style={{ textAlign: 'right' }}>Atual</th>
                <th style={{ textAlign: 'right' }} className="hide-m">
                  %
                </th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {(reportData.investments || []).map((inv: any) => (
                <tr key={inv.id}>
                  <td>
                    <strong>{inv.description}</strong>
                  </td>
                  <td className="hide-m">{formatInvestmentCategory(inv.category)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(inv.currentValue || 0)}</td>
                  <td style={{ textAlign: 'right' }} className="hide-m">
                    {Number(inv.yieldPercent || 0).toFixed(2)}%
                  </td>
                  <td className="hide-m">
                    <StatusCell status={inv.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'philanthropies') {
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Descrição</th>
                <th className="hide-m">Categoria</th>
                <th style={{ textAlign: 'right' }}>Doado</th>
                <th style={{ textAlign: 'right' }} className="hide-m">
                  Disponível
                </th>
              </tr>
            </thead>
            <tbody>
              {(reportData.philanthropies || reportData.items || []).map((donation: any) => (
                <tr key={donation.id}>
                  <td>
                    <strong>{donation.description}</strong>
                  </td>
                  <td className="hide-m">{formatPhilanthropyCategory(donation.category)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatCurrency(Number(donation.donatedAmount || 0))}
                  </td>
                  <td style={{ textAlign: 'right' }} className="hide-m">
                    {formatCurrency(
                      Number(
                        donation.availableAmount ??
                          Number(donation.amount || 0) - Number(donation.donatedAmount || 0)
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'rentals') {
      const rentals = (reportData.rentals || reportData.items || []) as any[];
      return (
        <div className="table-wrap">
          <table className="data-table compact-mobile">
            <thead>
              <tr>
                <th>Imóvel</th>
                <th className="hide-m">Inquilino</th>
                <th style={{ textAlign: 'right' }}>Mensal</th>
                <th className="hide-m">Status</th>
              </tr>
            </thead>
            <tbody>
              {rentals.map((r: any) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.asset?.description || r.assetDescription || r.assetId}</strong>
                  </td>
                  <td>{r.tenantName || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(Number(r.monthlyRent || 0))}</td>
                  <td className="hide-m">
                    <StatusCell status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <PageHeader
        breadcrumb="Relatórios / Resumos"
        title="Relatórios"
        subtitle="Filtros, resultados e exportação"
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Relatório</span>
            <select className="select" value={reportType} onChange={e => setReportType(e.target.value)}>
              {REPORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">De</span>
            <input
              type="date"
              className="input"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </label>
          <label className="filter-field">
            <span className="filter-label">Até</span>
            <input
              type="date"
              className="input"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </label>
          <label className="filter-field">
            <span className="filter-label">Agrupar</span>
            <select className="select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="type">Por tipo</option>
              <option value="month">Por mês</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-block-mobile"
          onClick={handleGenerateReport}
          disabled={isGenerating}
        >
          {isGenerating ? 'Gerando…' : 'Gerar relatório'}
        </button>
      </div>

      {isGenerating && (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" style={{ margin: '0 auto 12px' }} />
          <div className="muted">Gerando relatório…</div>
        </div>
      )}

      {!isGenerating && reportData && (
        <>
          {renderKpis()}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Resultado — {getReportTitle(selectedReport)}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleExportReport('excel')}
                  disabled={exportReportMutation.isLoading}
                >
                  Exportar Excel
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleExportReport('pdf')}
                  disabled={exportReportMutation.isLoading}
                >
                  Exportar PDF
                </button>
              </div>
            </div>
            {renderTable()}
          </div>
        </>
      )}

      {!isGenerating && !reportData && (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div className="muted">
            Selecione o relatório e o período, depois clique em <strong>Gerar relatório</strong>. O
            resultado aparece em tabela (não cards).
          </div>
        </div>
      )}
    </div>
  );
};

export default NewReports;
